import SETUP_WIZARDS_STEPS, { BASE_SETUP_STEPS, SETUP_WIZARDS, TABLET_STEPS } from '@/constants/setup-wizards'
import WizardAPIs from '@/API/helpers/wizard-api'
import USER_TYPES from '@/constants/authorization/user-types'
import { getFirstPendingWizardRoute, hasBusinessTypeHasStep } from '@/utils/setup-helper'
import router from '@/router'
import MarketingService, { MARKETING_EVENTS } from '@/services/marketing-service'
import { skipWizardByPackagePermission, skipWizardStepByPackagePermission } from '@/utils/permissions-helper'

const filterWizard = (wizard: any, isStepPassed: any, isPassed = 1) => Object.keys(wizard).filter((stepKey: any) => (isPassed ? isStepPassed(stepKey) : !isStepPassed(stepKey)))
const newsletterWizard = SETUP_WIZARDS_STEPS.NEWSLETTER
const viralWizard = SETUP_WIZARDS_STEPS.GOOGLE_MENU
const viralWizardSteps = viralWizard.steps.filter((step: string) => step !== 'viral_question')

export default {
  namespaced: true,
  state: {
    userWizard: {},
    currentStep: null,
    disableSubmit: false,
    extraFilters: null,
    completedWizard: false,
  },
  getters: {
    hideNewsletterWizard: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['subscription/freeSubscription'],
    currentBranchWizard: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['branches/currentBranch']?.wizard ?? {},
    isStepPassed: (state: any, getters: any, rootState: any, rootGetters: any) => (slug: string) => getters.currentBranchWizard[slug]?.passed || state.userWizard[slug]?.passed || (getters.hideNewsletterWizard && newsletterWizard.steps.includes(slug)) || (!rootGetters['subscription/isGoogleViralPurchased'] && viralWizardSteps.includes(slug)) || (rootGetters['subscription/isQr'] && TABLET_STEPS.includes(slug)) || skipWizardStepByPackagePermission(slug),
    isWizardPassed: (state: any, getters: any, rootState: any, rootGetters: any) => (wizard: string) => (SETUP_WIZARDS_STEPS[wizard]?.steps ?? []).every((slug: string) => getters.isStepPassed(slug) || !hasBusinessTypeHasStep(slug, rootGetters['businessTypes/isEnabled']) || skipWizardByPackagePermission(wizard)),
    userWizard: (state: any) => state.userWizard ?? {},
    isUserStep: (state: any) => (step: any) => state.userWizard[step] !== undefined,
    currentStep: (state: any) => state.currentStep ?? null,
    currentWizard: (state: any, getters: any) => Object.values(SETUP_WIZARDS).find((key: any) => (SETUP_WIZARDS_STEPS[key]?.steps ?? []).find((step: string) => getters.currentStep === step)),
    disableSubmit: (state: any) => state.disableSubmit,
    extraFilters: (state: any) => state.extraFilters,
    completedWizard: (state: any) => state.completedWizard,
    completedSteps: (state: any, getters: any) => filterWizard(getters.userWizard, getters.isStepPassed, 1).concat(filterWizard(getters.currentBranchWizard, getters.isStepPassed, 1)),
    uncompletedSteps: (state: any, getters: any) => filterWizard(getters.userWizard, getters.isStepPassed, 0).concat(filterWizard(getters.currentBranchWizard, getters.isStepPassed, 0)),
    baseCompleted: (state: any, getters: any) => BASE_SETUP_STEPS.every((step: string) => getters.currentBranchWizard[step]?.passed || state.userWizard[step]?.passed),
    completed: (state: any, getters: any) => getters.uncompletedSteps.length === 0,
  },
  mutations: {
    PASS_USER_WIZARD(state: any, key: string) {
      if (!state.userWizard[key]) {
        state.userWizard[key] = {}
      }
      state.userWizard[key] = {
        ...state.userWizard[key],
        passed: 1,
      }
    },
    SET_CURRENT_STEP(state: any, slug: string) {
      state.currentStep = slug
    },
    SET_USER_WIZARD(state: any, wizard: any) {
      state.userWizard = wizard
    },
    SET_EXTRA_FILTERS(state: any, filters?: object) {
      state.extraFilters = filters
    },
    DISABLE_SUBMIT(state: any, disable: boolean) {
      state.disableSubmit = disable
    },
    SET_COMPLETED_MODAL(state: any, visible: boolean) {
      state.completedWizard = visible
    },
  },
  actions: {
    async SEND_STEP(context: any, keys: string) {
      return context.dispatch('SEND_PASS_BRANCH_WIZARD', keys)
    },
    async SEND_PASS_BRANCH_WIZARD(context: any, keys: string) {
      await WizardAPIs.update(keys)
      keys.split(',').forEach((key: string) => {
        context.dispatch('PASS_STEP', key)
      })
    },
    async PASS_WIZARD_STEPS(context: any, keys: string) {
      keys.split(',').forEach((key: string) => {
        context.dispatch('PASS_STEP', key)
      })
    },
    async PASS_BRANCH_WIZARD(context: any, key: string) {
      const currentBranch = { ...(context.rootGetters['branches/currentBranch'] ?? {}) }
      if (!currentBranch.wizard) return
      if (!currentBranch?.wizard?.[key]) {
        currentBranch.wizard[key] = {}
      }
      currentBranch.wizard[key].passed = 1
      currentBranch.wizard = { ...currentBranch.wizard }
      await context.dispatch('branches/UPDATE_BRANCH', currentBranch, { root: true })
    },
    GET_FIRST_PENDING_WIZARD_ROUTE(context: any, filteredWizard?: string) {
      if (!context.rootGetters['permissions/isUserHasType'](USER_TYPES.BRANCH_ADMIN, false)) {
        return null
      }

      return getFirstPendingWizardRoute(context.getters.isWizardPassed, context.rootGetters['permissions/isUserHasType'], filteredWizard)
    },
    async PASS_STEP(context: any, slug: string) {
      if (!slug) return false
      const isPassed = context.getters.isStepPassed(slug)
      const isUserStep = context.getters.isUserStep(slug)
      if (isPassed) return false
      const wasAllCompleted = context.getters.completed

      if (isUserStep) {
        context.commit('PASS_USER_WIZARD', slug)
      } else {
        await context.dispatch('PASS_BRANCH_WIZARD', slug)
      }

      if (!context.rootGetters['permissions/isUserHasType'](USER_TYPES.OWNER)) {
        return true
      }

      if (context.getters.completed && !wasAllCompleted) {
        context.commit('SET_COMPLETED_MODAL', true)
        MarketingService.triggerEvent(MARKETING_EVENTS.WIZARD_COMPLETED, 'steps', 'steps completed')
        router.push({ name: 'admin:dashboard' }).catch(error => console.error(error))
      }

      return true
    },
  },
}
